import { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type Step3SelectTemplateProps = {
  selectedTemplateId: string;
  onSelect: (templateId: string) => void;
  wabaId?: string;
  systemIdFilter?: string;
};

const Step3SelectTemplate = ({
  selectedTemplateId,
  onSelect,
  wabaId,
  systemIdFilter,
}: Step3SelectTemplateProps) => {
  const { merchantId } = useContext(MerchantContext);

  const { data, isLoading } = trpc.whatsappTemplates.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  // Filter templates by wabaId and only show approved ones
  const availableTemplates = useMemo(() => {
    if (!data?.templates || !wabaId) return [];

    return data.templates.filter(
      (template) =>
        template.wabaId === wabaId &&
        template.status === "APPROVED" &&
        template.config.category !== "AUTHENTICATION" &&
        template.bodyPreview &&
        (!systemIdFilter || template.systemId === systemIdFilter)
    );
  }, [data?.templates, wabaId, systemIdFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="xl" />
          <p className="text-gray-600">Cargando plantillas...</p>
        </div>
      </div>
    );
  }

  if (!wabaId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="py-8 text-center text-gray-500">
            Por favor selecciona una conexión de WhatsApp primero.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (availableTemplates.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-2 py-8">
            <p className="text-center text-gray-500">
              {systemIdFilter
                ? "No hay plantillas de cobranza aprobadas disponibles para esta conexión de WhatsApp."
                : "No hay plantillas aprobadas disponibles para esta conexión de WhatsApp."}
            </p>
            <Link
              to="/configuration/templates"
              className="font-medium text-purple-500 hover:text-purple-600 hover:underline"
            >
              Ver plantillas
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getCategoryBadgeClass = (category: string) => {
    const categoryClasses: Record<string, string> = {
      MARKETING: "bg-purple-100 text-purple-800 border-purple-200",
      UTILITY: "bg-blue-100 text-blue-800 border-blue-200",
      AUTHENTICATION: "bg-orange-100 text-orange-800 border-orange-200",
    };
    return categoryClasses[category] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Selecciona una Plantilla</p>
        <p className="mb-4 text-sm text-gray-600">
          Elige la plantilla de WhatsApp aprobada que quieres enviar a los leads seleccionados.
        </p>
      </div>

      <RadioGroup value={selectedTemplateId} onValueChange={onSelect}>
        <div className="flex flex-col gap-3">
          {availableTemplates.map((template) => (
            <Card
              key={template.id}
              className={cn(
                "cursor-pointer border-2 transition-all hover:border-purple-300 hover:bg-purple-50/50",
                selectedTemplateId === template.id
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-200"
              )}
              onClick={() => onSelect(template.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <RadioGroupItem value={template.id} className="mt-1" />
                  <div className="flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <p className="font-medium">{template.name}</p>
                      <Badge variant="outline" className={cn("text-xs", getCategoryBadgeClass(template.category))}>
                        {{ MARKETING: "Marketing", UTILITY: "Utilidad", AUTHENTICATION: "Autenticación" }[template.category] ?? template.category}
                      </Badge>
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                        {{ APPROVED: "Aprobada", PENDING: "Pendiente", REJECTED: "Rechazada", DELETED: "Eliminada", FAILED: "Fallida" }[template.status] ?? template.status}
                      </Badge>
                    </div>
                    <p className="line-clamp-2 text-sm text-gray-600">
                      {template.bodyPreview || "Sin contenido disponible"}
                    </p>
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">
                        Idioma: {template.language.toUpperCase()}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default Step3SelectTemplate;
